/** Lado do tile em pixels. Puramente visual — o servidor só raciocina em coordenadas de grid. */
export const TILE_SIZE = 32;
/** Ticks por segundo do game loop (PRD §2). 20 TPS = 50ms por tick. */
export const DEFAULT_TICK_RATE = 20;
/**
 * Direções cardeais. Objeto congelado em vez de `enum` porque `const enum` não
 * sobrevive a `isolatedModules`, e um `enum` normal emitiria runtime desnecessário.
 */
export const Direction = {
    North: 0,
    East: 1,
    South: 2,
    West: 3,
};
/**
 * Deslocamento em tiles por direção. `y` cresce para baixo (convenção de tela),
 * então Norte é `dy: -1`.
 */
export const DIRECTION_VECTORS = {
    [Direction.North]: { dx: 0, dy: -1 },
    [Direction.East]: { dx: 1, dy: 0 },
    [Direction.South]: { dx: 0, dy: 1 },
    [Direction.West]: { dx: -1, dy: 0 },
};
export function isDirection(value) {
    return value === 0 || value === 1 || value === 2 || value === 3;
}
/**
 * Tipos de tile. O mapa trafega como um byte por tile, um chunk por vez.
 *
 * Os seis primeiros valores são os originais e **não podem mudar de número**: eles já
 * estão gravados nos arquivos `.gfmap` versionados e nos mapas exportados para o Tiled.
 * O que veio depois — os materiais de solo e os obstáculos naturais — entra no fim da
 * lista pelo mesmo motivo.
 *
 * A divisão entre "solo" e "obstáculo" é o que dá ao cenário um aspecto de lugar em vez
 * de tabuleiro: o solo varia em material (grama, terra, areia, calçamento) e desenha
 * transições irregulares com os vizinhos; o obstáculo é um objeto sobre o solo, com
 * altura própria, e não um bloco que substitui o chão.
 */
export const TileType = {
    /** Grama. O solo padrão do mundo aberto. */
    Floor: 0,
    Wall: 1,
    Water: 2,
    /** Leva ao andar de cima ao ser pisado. Ver `floorDelta`. */
    StairsUp: 3,
    /** Leva ao andar de baixo. */
    StairsDown: 4,
    /** Passagem para outra região. O destino não está no tile, está na tabela do mapa. */
    Portal: 5,
    // --- Solos, todos atravessáveis ---
    /** Terra batida: trilha, clareira, entorno de construção. */
    Dirt: 6,
    /** Areia: a margem de qualquer água parada. */
    Sand: 7,
    /** Calçamento: praça e interior de construção. */
    Pavement: 8,
    /** Rocha lisa do subsolo. Escura o bastante para a caverna não parecer campo aberto. */
    CaveFloor: 9,
    // --- Obstáculos naturais, todos bloqueantes ---
    /** Árvore. Copa alta, que transborda para o tile de trás. */
    Tree: 10,
    /** Pedra solta ou afloramento. */
    Rock: 11,
    /** Moita. Bloqueia como pedra, mas lê como vegetação. */
    Bush: 12,
    /**
     * Zona de proteção: piso consagrado onde ninguém é golpeado nem golpeia (PRD §6).
     *
     * É um **tipo de tile**, e não um retângulo declarado no cabeçalho do mapa, por três
     * razões que se somam. Ela viaja de graça no `MapChunk`, sem um campo novo no formato
     * nem no Welcome; ela tem a forma que o desenhista quiser, e não a de um retângulo; e o
     * jogador **vê** onde ela acaba, porque o piso é diferente — a alternativa seria uma
     * fronteira invisível descoberta ao morrer do lado de fora dela.
     *
     * Ver `World.isProtected` para o que a regra faz, e §11.3 (item 9) para o que a ausência
     * dela fazia: um Druid recém-nascido, parado no ponto de spawn sem ter dado um passo,
     * morto pelas criaturas que passeiam por ali.
     */
    Sanctuary: 13,
    // --- Mecanismos: tiles que **mudam** em jogo (PRD §15.2) ---
    //
    // São a primeira família de tile que não é fotografia. Até aqui o byte do tile descrevia
    // um terreno que nunca mudava, e §3.5 pôde decidir que "cada chunk viaja uma vez por
    // sessão e nunca é reenviado". Estes seis contrariam essa decisão, e a contrapartida é
    // o `TileUpdate 0x94`: sete bytes para o tile que mudou, em vez dos 256 do chunk inteiro.
    //
    // Os seis entram de uma vez, e não um por vez conforme a mecânica pedir, porque cada
    // tipo novo custa o mesmo pedágio — `isBlocking` aqui, `tiled.ts`, `client/art/tiles.ts`
    // e `client/render/terrain.ts`, quatro arquivos em três pacotes. Pagar uma vez é a
    // diferença entre uma bumpada de formato e seis.
    /** Porta fechada. Bloqueia; abrir troca por `DoorOpen` e manda `TileUpdate`. */
    DoorClosed: 14,
    /** Porta aberta. Atravessável, e **aberta para todos** — ver §15.4. */
    DoorOpen: 15,
    /**
     * Porta trancada: pede chave, nível ou vocação.
     *
     * Tipo próprio, e não `DoorClosed` com um sinalizador na tabela de features, porque a
     * diferença precisa ser **vista**: uma porta que não abre e parece igual a uma que abre
     * é indistinguível de um bug para quem está do lado de fora.
     */
    DoorLocked: 16,
    /**
     * Buraco no chão: quem pisa cai para o andar de baixo.
     *
     * Não é escada — a escada é simétrica e o buraco é de mão única. É por isso que
     * `validateWorld` exige saída no andar de destino: buraco sem volta não é desafio, é
     * aprisionamento, e a única correção depois seria teleportar o jogador.
     */
    Hole: 17,
    // O 18 era `RopeSpot`, o gancho de corda: com uma corda na mão, subia-se por ali. Saiu com
    // o sistema de corda inteiro — toda troca de andar do jogo é escada, e um andar cuja saída
    // depende de um item comprado é um andar que tranca quem não leu o balcão certo. O número
    // fica **vago**, e não é reaproveitado: id de tile que muda de significado reescreve em
    // silêncio todo mapa já salvo (ver `TILE_BY_GID`).
    /** Alavanca. Bloqueia como parede — é objeto de parede —, e é usada do tile vizinho. */
    Lever: 19,
    // --- O subsolo, que era só rocha e piso (PRD §15.6) --------------------------
    //
    // A caverna tinha dois materiais — `CaveFloor` e `Wall` — e cinco andares feitos do
    // mesmo autômato celular. O resultado é galeria com contorno orgânico e **nenhuma
    // identidade**: o andar 1 é o 4 repintado de nada, porque não há o que repintar. Um
    // jogador que atravessa quatro andares assim não sabe dizer onde esteve, e um lugar que
    // não se descreve não é um lugar.
    //
    // Os sete entram de uma vez pelo mesmo argumento dos seis mecanismos acima: cada tipo
    // novo custa o mesmo pedágio — `isBlocking` aqui, `tiled.ts`, `tileset-image.ts`,
    // `client/art/tiles.ts`, `client/render/terrain.ts` e o minimapa. Pagar uma vez é a
    // diferença entre uma rodada de arte e sete.
    /**
     * Lava. Bloqueia como água, e é a única fonte de **cor quente** do subsolo.
     *
     * Bloqueia em vez de ferir por uma razão de honestidade: dano por pisar exige aviso
     * antes do passo, e a predição do cliente decide o passo sozinha (§3.2). Um tile que
     * mata sem que o cliente saiba é um tile que mata pela latência.
     */
    Lava: 20,
    /** Cristal. Bloqueia; é o marco luminoso do fundo da caverna. */
    Crystal: 21,
    /** Cogumelo de caverna, do tamanho de uma pessoa. Bloqueia como moita. */
    Mushroom: 22,
    /** Teia. Bloqueia — é o que faz um ninho ser um labirinto e não uma sala com aranhas. */
    Web: 23,
    /** Estalagmite. A pedra do subsolo, com a silhueta que só o subsolo tem. */
    Stalagmite: 24,
    /**
     * Veio de minério na rocha.
     *
     * Bloqueia, e é família de **parede** e não de objeto: ele ocupa a massa de rocha, e
     * desenhá-lo como pedra solta sobre o chão diria que dá para andar em volta dele. É o
     * que faz uma galeria ler como mina em vez de buraco.
     */
    OreVein: 25,
    /** Chão de ossada: piso de caverna coberto de ossos. Atravessável, e é o chão da cripta. */
    Bones: 26,
    /**
     * Baú do mapa (PRD §15.4, §15.9).
     *
     * `FeatureKind.Chest` existe no formato desde a v3 e não tinha tile: o baú era uma linha
     * na tabela de features **sem nada na tela**, e o único jeito de achá-lo seria clicar no
     * chão por acaso. Bloqueia e é usado do tile vizinho, como a alavanca — pelo mesmo
     * motivo, que é ser um objeto e não um piso.
     */
    Chest: 27,
    /**
     * O barco atracado no cais (PRD §15.8).
     *
     * É o **veículo à vista**, e não o mecanismo: quem leva o jogador de uma ilha à outra é o
     * barqueiro que fica no trapiche ao lado dele (ver `systems/travel.ts`), e o barco existe
     * para que a travessia se anuncie antes de qualquer conversa. Um cais com um NPC parado
     * na ponta e mar vazio ao lado não diz que dali se navega; um casco amarrado diz.
     *
     * Assenta sobre água e **bloqueia**, como a água já bloqueava: ninguém anda no convés,
     * porque o embarque é a fala com o barqueiro e não um passo. Materialmente é uma peça
     * sobre o mar, na mesma família da árvore e da pedra, e é por isso que ele tem chão
     * próprio embaixo em vez de substituí-lo.
     */
    Boat: 28,
    // --- A cidade, que era pedra e vazio (PRD §15.8) -----------------------------
    //
    // A vila tinha exatamente três materiais: `Wall` no perímetro dos prédios, `Pavement` no
    // interior deles e `Dirt` na trilha que ligava os portões à praça. Nada mais. Uma cidade
    // desenhada com o vocabulário do campo aberto lê como campo aberto com caixas de pedra
    // em cima, e é isso que se via na tela: rua de terra batida entre construções sem uma
    // única peça de mobília, de cerca, de luz ou de letra.
    //
    // Os seis entram de uma vez pelo mesmo argumento dos seis mecanismos de §15.2 e dos sete
    // materiais do subsolo de §15.6: cada tipo novo custa o mesmo pedágio — `isBlocking`
    // aqui, `map/tiled.ts`, `map/tileset-image.ts`, `client/art/tiles.ts`, o minimapa e o
    // `mapshot`. Pagar uma vez é a diferença entre uma rodada de arte e seis.
    //
    // A repartição entre eles é a mesma do resto do arquivo: `Cobble` é **solo** e mistura
    // com os outros materiais; os cinco seguintes são **objetos sobre o solo**, com altura
    // própria e silhueta que transborda para o tile de trás.
    /**
     * Calçada de pedra: a rua da cidade.
     *
     * Tipo próprio, e não `Pavement` reaproveitado, porque a rua e o piso de dentro de casa
     * precisam ser **distinguíveis do lado de fora da porta**. Com um material só, a cidade
     * inteira — praça, interior de loja e rua — saía do mesmo cinza, e a leitura de "onde
     * acaba a casa e começa a rua" ficava por conta da parede: apagada a parede pela copa de
     * uma árvore ou pela beira da tela, não havia mais cidade nenhuma, só calçamento.
     *
     * Mistura como calçamento (mesmo `Material`, ver `art/tiles.ts`), então ela não custa os
     * 256 quadros de transição de um material novo — o que ela ganha é o desenho: pedra
     * arredondada e rejunte, contra a laje cortada do piso de dentro.
     */
    Cobble: 29,
    /**
     * Balcão de loja.
     *
     * Bloqueia, e é o ponto: o lojista fica **atrás** dele, e a conversa acontece por cima —
     * `INTERACT_RANGE` é 3, então o balcão separa sem impedir. Antes disto o jogador entrava
     * na loja e andava até ficar em cima do vendedor, o que não parece uma loja: parece um
     * cômodo com uma pessoa parada dentro.
     */
    Counter: 30,
    /**
     * Cerca de madeira: a divisa de um quintal.
     *
     * Bloqueia como moita, e existe para dar ao terreno de uma casa uma fronteira que não é
     * de pedra. Uma cidade em que a única coisa que separa dois lugares é muralha é uma
     * cidade militar; a cerca é o que faz um quintal ser de alguém.
     */
    Fence: 31,
    /** Caixaria e barril: a carga encostada na parede de dentro da loja e no cais. */
    Crate: 32,
    /**
     * Lampião de rua.
     *
     * Não ilumina nada — a superfície não tem sistema de luz (§15.6 pôs luz só no subsolo) —,
     * e entra mesmo assim porque o que ele resolve é **silhueta**: é a única coisa vertical de
     * uma rua reta, e é por ela que o olho mede a direção da via e a distância percorrida. É a
     * mesma função do poço no meio da praça, distribuída ao longo do calçamento.
     */
    Lamp: 33,
    /**
     * Placa.
     *
     * `FeatureKind.Sign` existe no formato desde a v3 e **nunca teve tile**: o texto de placa
     * era uma linha na tabela de features sem nada na tela, e a única forma de encontrá-la
     * seria olhar o chão por acaso. É a mesma classe de defeito que os seis mecanismos de
     * §15.2 tinham antes de ganharem desenho — a diferença é que aqui o que estava invisível
     * é a única fonte de **texto** do mundo (§15.8, "e falta texto").
     */
    Sign: 34,
    /**
     * Terreno que este cliente ainda não recebeu.
     *
     * Só existe do lado do cliente: o mapa chega por chunk (`MapChunk`), e até o chunk
     * correspondente chegar o tile precisa de **algum** valor. Bloqueante de propósito —
     * ver `isBlocking`.
     */
    Unknown: 255,
};
/**
 * Tiles que bloqueiam movimento. Fonte da verdade da colisão, usada pelo servidor.
 *
 * `Unknown` bloqueia porque a alternativa é pior: o cliente prevê o próprio passo antes
 * da confirmação do servidor (PRD §3.2), e tratar terreno não recebido como chão livre
 * faria a predição atravessar uma parede que ainda não chegou — o personagem andaria e
 * seria puxado de volta assim que o chunk aparecesse.
 *
 * Árvore, pedra e moita bloqueiam também a **linha de visão**, que é derivada desta
 * mesma função: um bosque em que as criaturas enxergam através dos troncos seria um
 * bosque que não serve de cobertura, e a floresta perderia a única função tática que tem.
 */
export function isBlocking(tile) {
    return (tile === TileType.Wall ||
        tile === TileType.Water ||
        tile === TileType.Tree ||
        tile === TileType.Rock ||
        tile === TileType.Bush ||
        // Porta fechada e trancada bloqueiam; aberta não. É a única diferença mecânica entre
        // os três, e é por isso que são três tipos e não um com estado guardado à parte.
        tile === TileType.DoorClosed ||
        tile === TileType.DoorLocked ||
        // Alavanca é objeto de parede: ela ocupa a célula inteira e é acionada de fora dela.
        tile === TileType.Lever ||
        // O subsolo de §15.6. Lava e veio de minério são massa; cristal, cogumelo, teia e
        // estalagmite são objetos sobre o chão — e os quatro bloqueiam pelo mesmo motivo que a
        // moita: obstáculo dentro da sala é o que transforma um salão vazio em terreno.
        tile === TileType.Lava ||
        tile === TileType.Crystal ||
        tile === TileType.Mushroom ||
        tile === TileType.Web ||
        tile === TileType.Stalagmite ||
        tile === TileType.OreVein ||
        // Baú é objeto, como a alavanca: ocupa a célula inteira e é aberto de fora dela.
        tile === TileType.Chest ||
        // O barco atracado. Ele fica sobre água, que já bloqueava — o tipo próprio existe para
        // o desenho, e bloquear é o que mantém a regra igual com ou sem casco no tile.
        tile === TileType.Boat ||
        // A mobília da cidade (§15.8). `Cobble` está fora desta lista de propósito — é rua, e
        // rua é para andar. Os cinco daqui são objetos sobre o chão, e bloqueiam pelo mesmo
        // motivo que a moita: obstáculo dentro do lugar é o que transforma um cômodo vazio em
        // loja e uma faixa de calçamento em rua com esquina.
        tile === TileType.Counter ||
        tile === TileType.Fence ||
        tile === TileType.Crate ||
        tile === TileType.Lamp ||
        tile === TileType.Sign ||
        tile === TileType.Unknown);
}
/**
 * O tile é massa de rocha — parede ou veio de minério?
 *
 * Existe porque o desenho da parede depende de **quem está em cima e embaixo** (ver
 * `faceOffset` no cliente): o coroamento iluminado só vai no tile mais alto da massa. Um
 * veio de minério no meio de uma parede que não contasse como parede abriria uma faixa
 * clara no meio da rocha, como se ali houvesse céu.
 */
export function isWallMass(tile) {
    return tile === TileType.Wall || tile === TileType.OreVein;
}
/** A porta em qualquer um dos três estados. */
export function isDoor(tile) {
    return (tile === TileType.DoorClosed ||
        tile === TileType.DoorOpen ||
        tile === TileType.DoorLocked);
}
/**
 * O tile é um material de solo — algo em que se anda, e não um objeto sobre o chão?
 *
 * Escada, portal e zona de proteção contam: os três são piso trabalhado, e quem pisa neles
 * continua vendo chão embaixo dos pés.
 */
export function isGround(tile) {
    return !isBlocking(tile) && tile !== TileType.Unknown;
}
/**
 * O tile protege quem está em cima dele?
 *
 * Fica no protocolo, e não só no servidor, porque as duas pontas precisam da resposta pelo
 * mesmo motivo que precisam de `isBlocking`: o servidor decide o combate com ela e o
 * cliente desenha o piso com ela. Duas cópias divergiriam no primeiro tipo novo de tile
 * seguro, e o sintoma seria um chão que parece santuário e não protege.
 */
export function isProtectedTile(tile) {
    return tile === TileType.Sanctuary;
}
/**
 * Quantos andares o tile faz subir ao ser pisado: `+1`, `-1` ou `0`.
 *
 * `z` cresce para cima — `z + 1` é o andar de cima. A convenção é oposta à do gênero
 * clássico (onde o subsolo tem número maior), e é assim de propósito: aqui `z` é índice
 * de vetor, e um índice que cresce ao descer inverteria o sinal de toda aritmética de
 * andar dentro do servidor sem ganhar nada.
 */
export function floorDelta(tile) {
    if (tile === TileType.StairsUp)
        return 1;
    if (tile === TileType.StairsDown)
        return -1;
    return 0;
}
/**
 * Teto de andares por região. O andar trafega como `u8` em vários pacotes, então o
 * limite real é 255; 16 é o limite *de projeto*, e serve para que um arquivo de mapa
 * corrompido seja recusado na carga em vez de alocar gigabytes de tiles.
 */
export const MAX_FLOORS = 16;
/**
 * Lado do chunk de mapa, em tiles. Potência de 2 para que a coordenada do chunk saia
 * de um deslocamento de bits.
 *
 * 16×16 = 256 bytes por chunk. Grande o bastante para que a tela inteira caiba em
 * poucos pacotes, pequeno o bastante para que atravessar uma fronteira de chunk não
 * dispare um envio perceptível.
 */
export const CHUNK_SHIFT = 4;
export const CHUNK_SIZE = 1 << CHUNK_SHIFT;
/**
 * Natureza da entidade. Vai no Snapshot porque o cliente precisa distinguir quem
 * desenhar como jogador e quem desenhar como criatura, e não há pacote de spawn
 * separado onde essa informação pudesse caber uma única vez.
 */
export const EntityKind = {
    Player: 0,
    Npc: 1,
    /**
     * Porta de casa (PRD §15.4).
     *
     * A única porta que é entidade e não tile: passagem **por pessoa** não é expressável num
     * byte compartilhado, e é a pureza de `isBlocking` que faz a predição do cliente acertar.
     * Como entidade ela cabe sem campo novo, porque o Snapshot já é calculado por observador —
     * "aberta para você" é a porta não entrar na sua Área de Interesse.
     */
    Door: 2,
};
/**
 * Vocações (PRD §9). Escolhidas na criação do personagem e imutáveis depois.
 *
 * O valor trafega no registro de entidade do Snapshot porque é o que diz ao cliente
 * qual atlas de sprite usar (§8.3) — sem isso ele não teria como desenhar um Knight
 * diferente de um Sorcerer, que é justamente por que hoje tudo é retângulo.
 *
 * Para NPCs este mesmo byte carrega o índice da espécie: o cliente precisa da mesma
 * informação ("qual atlas") para os dois, e um segundo campo só para criaturas seria
 * um byte por entidade por tick gasto para dizer a mesma coisa.
 */
export const Vocation = {
    Knight: 0,
    Paladin: 1,
    Druid: 2,
    Sorcerer: 3,
};
export function isVocation(value) {
    return value === 0 || value === 1 || value === 2 || value === 3;
}
export const VOCATION_NAMES = {
    [Vocation.Knight]: 'Knight',
    [Vocation.Paladin]: 'Paladin',
    [Vocation.Druid]: 'Druid',
    [Vocation.Sorcerer]: 'Sorcerer',
};
/**
 * Níveis visuais de equipamento (PRD §8.3). O personagem muda de aparência por faixa,
 * não peça a peça — o que mantém a composição em um atlas por vocação.
 */
export const OUTFIT_TIERS = 3;
/**
 * Skills que avançam por uso (PRD §6). Punho entra na lista apesar de não estar
 * nomeado no PRD: sem ele, um personagem desarmado não avançaria nada, e todo
 * personagem começa desarmado.
 */
export const SkillId = {
    Fist: 0,
    Sword: 1,
    Axe: 2,
    Club: 3,
    Distance: 4,
    Shield: 5,
    Magic: 6,
};
export const SKILL_COUNT = 7;
export const SKILL_NAMES = {
    [SkillId.Fist]: 'Punho',
    [SkillId.Sword]: 'Espada',
    [SkillId.Axe]: 'Machado',
    [SkillId.Club]: 'Clava',
    [SkillId.Distance]: 'Distância',
    [SkillId.Shield]: 'Escudo',
    [SkillId.Magic]: 'Magia',
};
/**
 * Peso e capacidade trafegam como inteiros nesta escala (centésimos de oz).
 *
 * Ponto flutuante em peso é uma armadilha conhecida: somar 0,1 oz vinte vezes não dá
 * 2 oz em binário, e a capacidade acabaria recusando um item por um erro de arredondamento
 * invisível. Inteiro na menor unidade elimina a classe inteira de bug.
 */
export const WEIGHT_SCALE = 100;
/**
 * Marca de PvP (PRD §6). O que a comunidade sabe sobre a conduta de um personagem.
 *
 * Viaja no registro de entidade do Snapshot porque **precisa ser vista antes do golpe**:
 * a regra que ela descreve — quem está marcado é alvo livre, quem agride sem motivo se
 * marca — só funciona se der para olhar para alguém e saber em qual dos dois casos se está.
 * Uma marca invisível seria a mesma classe de defeito que §11 passou inteiro consertando:
 * regra existente que o jogador não tem como perceber.
 */
export const PvpMark = {
    None: 0,
    /** Agrediu alguém que não o estava agredindo. */
    Aggressor: 1,
    /** Matou desmarcados o bastante para virar alvo livre. */
    Murderer: 2,
};
export function isPvpMark(value) {
    return value === 0 || value === 1 || value === 2;
}
/**
 * Efeitos visuais. O cliente escolhe a animação; o servidor só nomeia o que aconteceu.
 *
 * Mora aqui, e não em `spells.ts` onde nasceu, porque deixou de ser vocabulário só de
 * magia: condição (veneno, fogo, sangramento) e runa também nomeiam o que desenhar. Com a
 * tabela dentro de `spells.ts`, o módulo de condições precisaria importá-la e `spells.ts`
 * precisaria importar `ConditionSpec` de volta — um ciclo em tempo de execução para dizer
 * duas coisas que não dependem uma da outra.
 */
export const EffectId = {
    None: 0,
    Hit: 1,
    Fire: 2,
    Ice: 3,
    Energy: 4,
    Heal: 5,
    Roots: 6,
    Shout: 7,
    Charge: 8,
    /** Nuvem verde do veneno. */
    Poison: 9,
    /** Respingo do sangramento. */
    Bleed: 10,
    /** Projéteis (viajam de um ponto a outro). */
    Arrow: 20,
    MagicArrow: 21,
    Fireball: 22,
    /**
     * Bola de gelo: o par frio da `Fireball`.
     *
     * Existe porque o bastão do Druid precisava de um projétil e os três anteriores já
     * tinham dono — reaproveitar a `Fireball` faria fogo e gelo saírem da mesma cor, que é
     * a única coisa que o projétil tem para dizer antes de acertar. `Ice` (3) não serve:
     * ela é o estilhaço que **floresce no alvo**, e projétil e impacto são desenhos
     * diferentes do mesmo golpe — ver a faixa dos vinte, que existe só para os que viajam.
     */
    Iceball: 23,
};
/**
 * Elemento de uma fonte de dano (PRD §15.5).
 *
 * Todo dano tem elemento — o golpe de espada é `Physical`, a Bola de Fogo é `Fire`, o
 * veneno é `Earth` —, e toda criatura tem um vetor de resistências. É o campo que só é
 * **barato hoje**: acrescentá-lo depois de vinte e cinco espécies escritas exigiria
 * revisitar as vinte e cinco, e cada fonte de dano que nascer sem elemento nasce com um
 * elemento implícito que alguém vai ter que descobrir lendo o código.
 *
 * O que ele compra é a diferença entre "o andar 4 tem o mesmo Troll do andar 1 com mais
 * vida" e "o andar 4 pede outra coisa da sua mochila".
 */
export const DamageElement = {
    Physical: 0,
    Fire: 1,
    Ice: 2,
    Energy: 3,
    /** Veneno e terra. O elemento das condições que corroem. */
    Earth: 4,
    /** Cura e luz. Uma resistência alta aqui é uma criatura que a cura fere. */
    Holy: 5,
    Death: 6,
};
export const DAMAGE_ELEMENT_COUNT = 7;
export const ELEMENT_NAMES = {
    [DamageElement.Physical]: 'Físico',
    [DamageElement.Fire]: 'Fogo',
    [DamageElement.Ice]: 'Gelo',
    [DamageElement.Energy]: 'Energia',
    [DamageElement.Earth]: 'Terra',
    [DamageElement.Holy]: 'Sagrado',
    [DamageElement.Death]: 'Morte',
};
/**
 * Resistência neutra, em porcentagem. 100 = o dano passa inteiro.
 *
 * A regra de dimensionamento que §15.5 impõe, e que existe para o elemento não virar um
 * segundo eixo de progressão escondido: **todas as criaturas nascem em 100% no primeiro
 * dia**, e até o nível 20 nenhuma resistência sai da faixa de 90 a 110. Antes disso o
 * jogador ainda não tem dois elementos de dano na mão, e uma resistência de 50% seria um
 * muro que ele não tem como derrubar nem como entender.
 */
export const NEUTRAL_RESISTANCE = 100;
export const MIN_EARLY_RESISTANCE = 90;
export const MAX_EARLY_RESISTANCE = 110;
/** Nível a partir do qual uma resistência pode sair da faixa estreita. Ver acima. */
export const ELEMENT_FREEDOM_LEVEL = 20;
/**
 * Aplica a resistência a um dano. Arredonda para baixo, com piso de 1 quando havia dano.
 *
 * O piso existe para que resistência nunca vire imunidade por arredondamento: um golpe que
 * acertou e não tirou nada é indistinguível de um golpe que errou, e o jogador conclui que
 * a arma está quebrada.
 */
export function applyResistance(damage, resistance) {
    if (damage <= 0)
        return 0;
    const reduced = Math.floor((damage * resistance) / 100);
    return resistance > 0 ? Math.max(1, reduced) : 0;
}
// --- Sustento: saciedade e stamina (PRD §15.7) -------------------------------
//
// Os dois são relógios em milissegundos que só correm **online e fora de zona de
// proteção**, e é a mesma condição pelas mesmas duas razões. Consumir offline puniria quem
// fechou o navegador, que é o oposto do que os dois existem para fazer — a saciedade quer
// que o jogador volte com comida na mochila, a stamina quer que ele volte amanhã. E
// consumir dentro da zona de proteção puniria exatamente quem está organizando mochila,
// negociando ou comprando, que é o gesto que §13.7 pôs dentro da praça de propósito.
//
// Moram no protocolo, e não só no servidor, porque o HUD desenha os dois: sem o número na
// tela a penalidade é invisível e vira reclamação de bug.
/**
 * Teto rígido de saciedade, em ms (PRD §15.7). Vinte minutos.
 *
 * Rígido no sentido literal: comer com o contador cheio **recusa e não consome o item**.
 * Sem essa regra o jogador queima um presunto por engano, e uma mecânica de logística vira
 * punição por descuido de clique.
 *
 * Vinte minutos é pequeno de propósito. Dois presuntos enchem o contador e custam 10 oz de
 * 400 — a decisão de carga existe, e ela compete com poção e com loot, que é onde a
 * saciedade vira interessante em vez de virar imposto.
 */
export const FOOD_MAX_MS = 1_200_000;
/**
 * Stamina cheia, em ms. Quarenta e duas horas.
 *
 * O único limitador de progressão que havia era quantas horas alguém aguenta clicar, num
 * jogo com respawn temporizado e um loadbot versionado no próprio repositório. A stamina é
 * o único freio que quase não incomoda o jogador humano e destrói o bot: quarenta e duas
 * horas são mais do que qualquer pessoa joga numa semana e menos do que um bot gasta em
 * dois dias.
 *
 * O número é grande **e** o relógio é lento de propósito. Um teto pequeno viraria um
 * cronômetro que o jogador olha; este é um limite que ele descobre só depois de exagerar.
 */
export const STAMINA_MAX_MS = 42 * 60 * 60 * 1000;
export const STAMINA_BANDS = [
    { minMs: 14 * 60 * 60 * 1000, multiplier: 1.0, label: 'descansado' },
    { minMs: 1, multiplier: 0.5, label: 'cansado' },
    { minMs: 0, multiplier: 0, label: 'exausto' },
];
/** A faixa em que este relógio de stamina cai. Nunca `undefined`: a última tem piso 0. */
export function staminaBand(staminaMs) {
    for (const band of STAMINA_BANDS) {
        if (staminaMs >= band.minMs)
            return band;
    }
    // Inalcançável — a última faixa tem piso 0 e `staminaMs` nunca é negativo —, mas o
    // compilador não sabe disso e um `!` esconderia o dia em que alguém reordenar a tabela.
    return STAMINA_BANDS[STAMINA_BANDS.length - 1];
}
/**
 * Quanto de stamina um ms offline devolve (PRD §15.7).
 *
 * Um terço: três horas fora do jogo devolvem uma hora de caça. É o único sistema do jogo
 * que faz **estar offline ser produtivo**, e é isso que faz a pessoa voltar amanhã em vez
 * de esticar hoje até enjoar.
 *
 * A razão é menor que 1 de propósito. Com 1:1, deslogar por meia hora anularia meia hora de
 * caça e a stamina deixaria de ser um teto diário para virar um imposto de pausa.
 *
 * **Um terço é o número que define o ponto de equilíbrio**, e é ele que faz o sistema
 * cumprir o que §15.7 encomenda. Quem joga seis horas por dia gasta seis e recupera as
 * outras dezoito a um terço — seis. Empata, e joga assim para sempre sem nunca ver uma
 * penalidade. Quem joga doze gasta doze e recupera quatro: perde oito por dia, e em quatro
 * dias cruza o degrau. É essa assimetria que comprime a distância entre as duas pessoas sem
 * proibir nada a nenhuma das duas.
 */
export const STAMINA_REGEN_RATIO = 1 / 3;
/** `targetId` com este valor limpa o alvo em vez de selecionar alguém. */
export const NO_TARGET = 0;
/** Alcance do ataque corpo a corpo, em tiles (distância de Chebyshev). */
export const MELEE_RANGE = 1;
/**
 * Postura de combate (PRD §5).
 *
 * O auto-attack decide *quando* golpear e a arma decide *quanto*; o que faltava era o
 * jogador poder dizer **como** — se aquela briga é para matar depressa ou para aguentar em
 * pé. Sem isso, um Knight cercado por três Trolls e um Knight limpando um Rato lutam com
 * exatamente os mesmos números, e a única decisão de combate que sobra é fugir.
 *
 * São três e não duas porque `Balanced` é o jogo como ele era: multiplicador 1 nos dois
 * lados, sem perseguição. Quem não abrir o painel continua com o combate que já conhecia, e
 * as duas posturas novas ficam de cada lado dele — o que também dá ao par um meio-termo para
 * onde voltar, em vez de obrigar a escolher entre dois extremos.
 *
 * A perseguição anda junto da postura ofensiva de propósito. Ela é o mesmo pedido dito de
 * outro jeito — "eu quero **fechar** essa distância" —, e um segundo interruptor separado
 * criaria a combinação "defesa máxima e vá atrás dele", que é uma ordem contra si mesma.
 */
export const CombatStance = {
    /** Bate mais, apara menos, e vai atrás do alvo selecionado. */
    Offensive: 0,
    /** Os números crus da arma e do escudo, sem correção. O padrão. */
    Balanced: 1,
    /** Apara mais e bate menos, sem sair do lugar. */
    Defensive: 2,
};
/** Com que postura o personagem entra no mundo. Ver `STANCES`. */
export const DEFAULT_STANCE = CombatStance.Balanced;
/**
 * Os multiplicadores, num lugar só e nas duas pontas.
 *
 * O servidor é quem os aplica — ele é a autoridade sobre dano —, mas o cliente precisa dos
 * **mesmos** números para o botão poder dizer o que faz. A alternativa era uma frase escrita
 * à mão na interface, que é uma segunda cópia da regra livre para envelhecer em silêncio no
 * dia em que o balanceamento mudar.
 *
 * A defesa vale metade do seu valor no cálculo do golpe (ver `rollDamage`), então um
 * multiplicador de 1,5 na defesa é mais brando do que parece — e é por isso que ele pode ser
 * maior que a perda de ataque sem tornar o defensor imortal. Nenhuma postura zera nada: um
 * personagem que não consegue mais ferir nada é um personagem que só pode fugir, e essa já é
 * uma opção que ele tem sem precisar de um botão.
 */
export const STANCES = {
    [CombatStance.Offensive]: {
        label: 'ataque',
        hint: '+25% de ataque, −40% de defesa, e o personagem vai atrás do alvo',
        attack: 1.25,
        defense: 0.6,
        chase: true,
    },
    [CombatStance.Balanced]: {
        label: 'equilíbrio',
        hint: 'ataque e defesa como a arma e o escudo dão',
        attack: 1,
        defense: 1,
        chase: false,
    },
    [CombatStance.Defensive]: {
        label: 'defesa',
        hint: '+50% de defesa, −40% de ataque, e o personagem fica onde está',
        attack: 0.6,
        defense: 1.5,
        chase: false,
    },
};
export function isCombatStance(value) {
    return value === CombatStance.Offensive
        || value === CombatStance.Balanced
        || value === CombatStance.Defensive;
}
/**
 * Distância de Chebyshev — o número de passos em grid com diagonais permitidas.
 *
 * É a métrica certa para alcance em mapa de tiles: com ela os oito vizinhos de um
 * tile estão todos à distância 1, que é como um jogador enxerga "estar do lado".
 * A distância euclidiana diria que a diagonal está a 1,41 e o ataque falharia em
 * quatro das oito posições adjacentes, sem explicação visível na tela.
 */
export function chebyshevDistance(ax, ay, bx, by) {
    return Math.max(Math.abs(ax - bx), Math.abs(ay - by));
}
//# sourceMappingURL=constants.js.map